/**
 * Canonical healthcare profession taxonomy for Clinivo — FRONTEND MIRROR.
 * ======================================================================
 *
 * The backend authority lives in `base44/shared/professionTaxonomy.ts`. The
 * platform does not ship `base44/` to the browser, so this file must exist as a
 * frontend copy — it is the ONLY permitted copy, and no component may define
 * its own profession dictionary.
 *
 * KEEP IN SYNC: `TAXONOMY_VERSION` must match the backend module. If the two
 * versions differ, this mirror has drifted and must be regenerated from the
 * backend file. Import from here everywhere in the UI:
 *
 *   import { getCanonicalProfession, getProfessionParent } from '@/lib/professionTaxonomy';
 *
 * Design rules (identical to the backend module)
 * ---------------------------------------------
 * 1. RAW SOURCE DATA IS NEVER MODIFIED. `title`, `specialty`, descriptions and
 *    every imported value stay exactly as received. The canonical profession is
 *    a pure read-time projection computed from those raw values.
 * 2. ACCURACY OVER COVERAGE. A profession is returned only when the evidence is
 *    an explicit credential, modality or occupation phrase. Broad labels
 *    ("Therapy", "Allied Health Professional", "Nursing", "Radiology",
 *    "Cath Lab", "Other") intentionally resolve to nothing on their own — they
 *    are containers, not professions.
 * 3. EVIDENCE ORDER. Title aliases first, then the specialty field, then
 *    explicit credential text in `requirements`. The specialty field carries the
 *    feed's richest structured signal, so a *broad* title bucket defers to it
 *    (see broad_title_aliases).
 * 4. Deterministic. The same job always produces the same result.
 */

export const TAXONOMY_VERSION = '1.0.0';

/* ------------------------------------------------------------------ *
 * Parent categories
 * ------------------------------------------------------------------ */

export const PROFESSION_CATEGORIES = {
  nursing: 'Nursing',
  advanced_practice: 'Advanced Practice',
  physician: 'Physician',
  therapy: 'Therapy',
  imaging: 'Imaging',
  cardiovascular: 'Cardiovascular',
  laboratory: 'Laboratory',
  pharmacy: 'Pharmacy',
  behavioral_health: 'Behavioral Health',
  perioperative: 'Perioperative',
  clinical_support: 'Clinical Support'
};

/* ------------------------------------------------------------------ *
 * Professions
 *
 * aliases              — explicit credential / modality / occupation phrases.
 *                        Matched as whole-word phrases in `title` AND in each
 *                        comma-separated `specialty` token.
 * broad_title_aliases  — a subset of aliases that describe a *bucket* rather
 *                        than a single profession. When one of these matches the
 *                        title and the specialty resolves to a different
 *                        profession, the specialty wins (and the job is
 *                        reported as a deferral, not a conflict).
 * related_professions  — slugs, for internal linking.
 * related_specialties  — clinical settings/modalities commonly paired with the
 *                        profession (feed specialty vocabulary), for linking.
 * ------------------------------------------------------------------ */

export const PROFESSIONS = [
  /* ---------------- Nursing ---------------- */
  {
    slug: 'registered-nurse',
    display_name: 'Registered Nurse',
    parent_category: 'nursing',
    aliases: [
      'rn registered nurse', 'registered nurse', 'charge rn', 'don director of nursing',
      'director of nursing', 'staff nurse', 'rn'
    ],
    broad_title_aliases: [],
    related_professions: ['licensed-practical-nurse', 'certified-nursing-assistant', 'nurse-practitioner'],
    related_specialties: ['ICU', 'ER- Emergency Room', 'Medical/Surgical', 'Telemetry', 'Long Term Care', 'Corrections', 'L&D- Labor and Delivery', 'PACU', 'NICU- Neonatal Intensive Care Unit', 'Oncology']
  },
  {
    slug: 'licensed-practical-nurse',
    display_name: 'Licensed Practical Nurse',
    parent_category: 'nursing',
    aliases: [
      'lpn lvn licensed practical vocational nurse', 'licensed practical nurse',
      'licensed vocational nurse', 'lpn lvn', 'lpn', 'lvn'
    ],
    broad_title_aliases: [],
    related_professions: ['registered-nurse', 'certified-nursing-assistant'],
    related_specialties: ['Long Term Care', 'Corrections', 'Medical/Surgical', 'Clinic']
  },
  {
    slug: 'certified-nursing-assistant',
    display_name: 'Certified Nursing Assistant',
    parent_category: 'nursing',
    aliases: ['certified nursing assistant', 'nursing assistant', 'cna'],
    broad_title_aliases: [],
    related_professions: ['registered-nurse', 'licensed-practical-nurse'],
    related_specialties: ['Long Term Care', 'Skilled Nursing- SNF-LTC-ALF-ILF', 'Rehab']
  },

  /* ---------------- Advanced Practice ---------------- */
  {
    slug: 'nurse-practitioner',
    display_name: 'Nurse Practitioner',
    parent_category: 'advanced_practice',
    aliases: ['nurse practitioner', 'aprn', 'crnp', 'np'],
    broad_title_aliases: [],
    related_professions: ['registered-nurse', 'physician', 'physician-assistant'],
    related_specialties: ['Clinic', 'Outpatient', 'ER- Emergency Room', 'Oncology']
  },
  {
    slug: 'physician-assistant',
    display_name: 'Physician Assistant',
    parent_category: 'advanced_practice',
    aliases: ['physician assistant', 'physician associate', 'pa c'],
    broad_title_aliases: [],
    related_professions: ['nurse-practitioner', 'physician'],
    related_specialties: ['Clinic', 'Outpatient', 'OR- Operating Room']
  },

  /* ---------------- Physician ---------------- */
  {
    slug: 'physician',
    display_name: 'Physician',
    parent_category: 'physician',
    aliases: [
      'physician', 'hospitalist', 'intensivist', 'anesthesiologist', 'cardiologist',
      'oncologist', 'neurologist', 'psychiatrist', 'surgeon'
    ],
    broad_title_aliases: [],
    related_professions: ['nurse-practitioner', 'physician-assistant'],
    related_specialties: ['Oncology', 'Cardiology', 'Pediatrics', 'Transplant']
  },

  /* ---------------- Therapy ---------------- */
  {
    slug: 'physical-therapist',
    display_name: 'Physical Therapist',
    parent_category: 'therapy',
    aliases: ['physical therapist', 'physical therapy', 'dpt'],
    broad_title_aliases: [],
    related_professions: ['physical-therapist-assistant', 'occupational-therapist'],
    related_specialties: ['Rehab', 'Outpatient', 'Inpatient', 'Home Health']
  },
  {
    slug: 'physical-therapist-assistant',
    display_name: 'Physical Therapist Assistant',
    parent_category: 'therapy',
    aliases: ['physical therapist assistant', 'pta'],
    broad_title_aliases: [],
    related_professions: ['physical-therapist'],
    related_specialties: ['Rehab', 'Outpatient', 'Inpatient', 'Skilled Nursing- SNF-LTC-ALF-ILF']
  },
  {
    slug: 'occupational-therapist',
    display_name: 'Occupational Therapist',
    parent_category: 'therapy',
    aliases: ['occupational therapist', 'occupational therapy', 'otr'],
    broad_title_aliases: [],
    related_professions: ['certified-occupational-therapy-assistant', 'physical-therapist'],
    related_specialties: ['Rehab', 'Outpatient', 'Inpatient', 'Pediatrics']
  },
  {
    slug: 'certified-occupational-therapy-assistant',
    display_name: 'Certified Occupational Therapy Assistant',
    parent_category: 'therapy',
    aliases: ['certified occupational therapy assistant', 'cota', 'ota'],
    broad_title_aliases: [],
    related_professions: ['occupational-therapist'],
    related_specialties: ['Rehab', 'Skilled Nursing- SNF-LTC-ALF-ILF', 'Outpatient']
  },
  {
    slug: 'speech-language-pathologist',
    display_name: 'Speech-Language Pathologist',
    parent_category: 'therapy',
    aliases: [
      'slp speech language pathologist', 'speech language pathologist',
      'speech therapist', 'ccc slp', 'slp'
    ],
    broad_title_aliases: [],
    related_professions: ['occupational-therapist', 'physical-therapist'],
    related_specialties: ['Pediatrics', 'Rehab', 'Outpatient', 'Skilled Nursing- SNF-LTC-ALF-ILF']
  },
  {
    slug: 'respiratory-therapist',
    display_name: 'Respiratory Therapist',
    parent_category: 'therapy',
    aliases: ['respiratory therapist', 'respiratory therapy', 'rrt'],
    broad_title_aliases: [],
    related_professions: ['registered-nurse'],
    related_specialties: ['ICU- Intensive Care Unit', 'NICU- Neonatal Intensive Care Unit', 'Pediatrics', 'ER- Emergency Room']
  },

  /* ---------------- Imaging ---------------- */
  {
    slug: 'radiologic-technologist',
    display_name: 'Radiologic Technologist',
    parent_category: 'imaging',
    aliases: [
      'radiologic technologist', 'radiologic technology', 'xray technologist',
      'xray tech', 'x ray technologist', 'x ray tech', 'radiographer', 'rad tech'
    ],
    broad_title_aliases: [],
    related_professions: ['ct-technologist', 'mri-technologist', 'mammography-technologist', 'interventional-radiology-technologist'],
    related_specialties: ['Radiology', 'ER- Emergency Room', 'Orthopedics', 'Outpatient']
  },
  {
    slug: 'ct-technologist',
    display_name: 'CT Technologist',
    parent_category: 'imaging',
    aliases: ['ct xray technologist', 'ct technologist', 'cat scan technologist', 'cat scan tech', 'ct tech'],
    broad_title_aliases: [],
    related_professions: ['radiologic-technologist', 'mri-technologist', 'pet-ct-technologist'],
    related_specialties: ['Radiology', 'ER- Emergency Room', 'Cardiology', 'Outpatient']
  },
  {
    slug: 'mri-technologist',
    display_name: 'MRI Technologist',
    parent_category: 'imaging',
    aliases: ['mri technologist', 'magnetic resonance technologist', 'mri tech', 'mri'],
    broad_title_aliases: [],
    related_professions: ['radiologic-technologist', 'ct-technologist'],
    related_specialties: ['Radiology', 'Outpatient', 'Neuro Tele']
  },
  {
    slug: 'mammography-technologist',
    display_name: 'Mammography Technologist',
    parent_category: 'imaging',
    aliases: ['mammography technologist', 'mammography tech', 'mammographer', 'mammography'],
    broad_title_aliases: [],
    related_professions: ['radiologic-technologist', 'sonographer'],
    related_specialties: ['Outpatient', 'Clinic', 'Oncology']
  },
  {
    slug: 'nuclear-medicine-technologist',
    display_name: 'Nuclear Medicine Technologist',
    parent_category: 'imaging',
    aliases: ['nuclear medicine technologist', 'nuclear medicine tech', 'nuclear medicine'],
    broad_title_aliases: [],
    related_professions: ['pet-ct-technologist', 'radiologic-technologist'],
    related_specialties: ['Radiology', 'Cardiology', 'Oncology', 'Outpatient']
  },
  {
    slug: 'pet-ct-technologist',
    display_name: 'PET/CT Technologist',
    parent_category: 'imaging',
    aliases: ['pet ct technologist', 'pet ct tech', 'pet ct'],
    broad_title_aliases: [],
    related_professions: ['nuclear-medicine-technologist', 'ct-technologist'],
    related_specialties: ['Radiology', 'Oncology', 'Outpatient']
  },
  {
    slug: 'interventional-radiology-technologist',
    display_name: 'Interventional Radiology Technologist',
    parent_category: 'imaging',
    aliases: [
      'ir tech interventional radiology', 'interventional radiology technologist',
      'interventional radiology tech', 'ir interventional radiology',
      'interventional radiology', 'ir tech'
    ],
    broad_title_aliases: [],
    related_professions: ['radiologic-technologist', 'cath-lab-technologist'],
    related_specialties: ['IR- Interventional Radiology', 'Radiology', 'Cardiology', 'OR- Operating Room']
  },
  {
    slug: 'sonographer',
    display_name: 'Sonographer',
    parent_category: 'imaging',
    aliases: [
      'ultrasound echo tech', 'ultrasound vascular tech', 'ultrasound technologist',
      'ultrasound tech', 'echo technologist', 'echo tech', 'vascular technologist',
      'vascular tech', 'sonographer', 'ultrasonographer', 'rdms'
    ],
    broad_title_aliases: [],
    related_professions: ['radiologic-technologist', 'cath-lab-technologist', 'mammography-technologist'],
    related_specialties: ['Ultrasound Tech', 'Cardiac', 'Vascular', 'L&D- Labor and Delivery', 'Outpatient']
  },
  {
    slug: 'radiation-therapist',
    display_name: 'Radiation Therapist',
    parent_category: 'imaging',
    aliases: ['radiation therapist', 'radiation therapy'],
    broad_title_aliases: [],
    related_professions: ['radiologic-technologist', 'nuclear-medicine-technologist'],
    related_specialties: ['Oncology', 'Chemotherapy', 'Radiology', 'Outpatient']
  },

  /* ---------------- Cardiovascular ---------------- */
  {
    slug: 'cath-lab-technologist',
    display_name: 'Cath Lab Technologist',
    parent_category: 'cardiovascular',
    aliases: ['cath lab technologist', 'cath lab tech', 'cardiac cath technologist'],
    broad_title_aliases: [],
    related_professions: ['electrophysiology-technologist', 'interventional-radiology-technologist', 'radiologic-technologist'],
    related_specialties: ['Cath Lab', 'Cardiac', 'Cardiology', 'CVOR- Cardiovascular Operating Room']
  },
  {
    slug: 'electrophysiology-technologist',
    display_name: 'Electrophysiology Technologist',
    parent_category: 'cardiovascular',
    aliases: [
      'electrophysiology tech ep tech', 'electrophysiology technologist',
      'electrophysiology tech', 'electrophysiology', 'ep tech'
    ],
    broad_title_aliases: [],
    related_professions: ['cath-lab-technologist', 'radiologic-technologist'],
    related_specialties: ['Electrophysiology', 'Cath Lab', 'Cardiology', 'Radiology']
  },

  /* ---------------- Laboratory ---------------- */
  {
    slug: 'medical-laboratory-technician',
    display_name: 'Medical Laboratory Technician',
    parent_category: 'laboratory',
    aliases: [
      'medical laboratory technician', 'medical laboratory tech',
      'medical lab technician', 'medical lab tech', 'clinical laboratory technician', 'mlt'
    ],
    broad_title_aliases: [],
    related_professions: ['histology-technician', 'pathology-assistant'],
    related_specialties: ['Laboratory', 'Clinic', 'Outpatient']
  },
  {
    slug: 'histology-technician',
    display_name: 'Histology Technician',
    parent_category: 'laboratory',
    aliases: ['histology technician', 'histology tech', 'histotechnician', 'histology'],
    broad_title_aliases: [],
    related_professions: ['medical-laboratory-technician', 'pathology-assistant'],
    related_specialties: ['Laboratory', 'Oncology']
  },
  {
    slug: 'pathology-assistant',
    display_name: 'Pathology Assistant',
    parent_category: 'laboratory',
    aliases: ['pathology assistant', 'pathologists assistant'],
    broad_title_aliases: [],
    related_professions: ['histology-technician', 'medical-laboratory-technician'],
    related_specialties: ['Laboratory', 'Oncology', 'OR- Operating Room']
  },

  /* ---------------- Pharmacy ---------------- */
  {
    slug: 'pharmacist',
    display_name: 'Pharmacist',
    parent_category: 'pharmacy',
    aliases: ['pharmacist', 'pharmd', 'rph'],
    broad_title_aliases: [],
    related_professions: ['pharmacy-technician'],
    related_specialties: ['Pharmacy', 'Oncology', 'Clinic', 'Outpatient']
  },
  {
    slug: 'pharmacy-technician',
    display_name: 'Pharmacy Technician',
    parent_category: 'pharmacy',
    aliases: ['pharmacy technician', 'pharmacy tech', 'cpht'],
    broad_title_aliases: [],
    related_professions: ['pharmacist'],
    related_specialties: ['Pharmacy', 'Clinic', 'Outpatient']
  },

  /* ---------------- Behavioral Health ---------------- */
  {
    slug: 'social-worker',
    display_name: 'Social Worker',
    parent_category: 'behavioral_health',
    aliases: [
      'licensed clinical social worker', 'social worker', 'social work',
      'lcsw', 'lmsw', 'msw'
    ],
    // "Social Work" is the feed's bucket label: it covers LCSW, LPCC and
    // Psychologist roles alike, so it defers to the specialty field.
    broad_title_aliases: ['social work'],
    related_professions: ['licensed-professional-counselor', 'psychologist'],
    related_specialties: ['Corrections', 'Behavioral Health', 'Inpatient', 'Case Manager', 'Outpatient']
  },
  {
    slug: 'licensed-professional-counselor',
    display_name: 'Licensed Professional Counselor',
    parent_category: 'behavioral_health',
    aliases: [
      'licensed professional clinical counselor', 'licensed professional counselor',
      'licensed clinical professional counselor', 'mental health counselor', 'lpcc', 'lpc'
    ],
    broad_title_aliases: [],
    related_professions: ['social-worker', 'psychologist'],
    related_specialties: ['Corrections', 'Behavioral Health', 'Inpatient', 'Outpatient']
  },
  {
    slug: 'psychologist',
    display_name: 'Psychologist',
    parent_category: 'behavioral_health',
    aliases: ['psychologist', 'psyd'],
    broad_title_aliases: [],
    related_professions: ['licensed-professional-counselor', 'social-worker'],
    related_specialties: ['Corrections', 'Behavioral Health', 'Pediatrics', 'Outpatient']
  },

  /* ---------------- Perioperative ---------------- */
  {
    slug: 'surgical-technologist',
    display_name: 'Surgical Technologist',
    parent_category: 'perioperative',
    aliases: [
      'first assist surgical tech', 'surgical technologist', 'surgical tech',
      'operating room tech', 'scrub tech', 'cst'
    ],
    broad_title_aliases: [],
    related_professions: ['surgical-first-assistant', 'sterile-processing-technician', 'registered-nurse'],
    related_specialties: ['OR- Operating Room', 'CVOR- Cardiovascular Operating Room', 'Orthopedics', 'First Assist']
  },
  {
    slug: 'surgical-first-assistant',
    display_name: 'Surgical First Assistant',
    parent_category: 'perioperative',
    aliases: ['surgical first assistant', 'first assist', 'first assistant', 'csfa'],
    broad_title_aliases: [],
    related_professions: ['surgical-technologist'],
    related_specialties: ['OR- Operating Room', 'First Assist', 'CVOR- Cardiovascular Operating Room']
  },
  {
    slug: 'sterile-processing-technician',
    display_name: 'Sterile Processing Technician',
    parent_category: 'perioperative',
    aliases: ['sterile processing technician', 'sterile processing tech', 'spd tech', 'central sterile'],
    broad_title_aliases: [],
    related_professions: ['surgical-technologist'],
    related_specialties: ['OR- Operating Room', 'Sterile Processing Tech']
  },

  /* ---------------- Clinical Support ---------------- */
  {
    slug: 'medical-assistant',
    display_name: 'Medical Assistant',
    parent_category: 'clinical_support',
    aliases: ['certified medical assistant', 'medical assistant', 'cma'],
    broad_title_aliases: [],
    related_professions: ['licensed-practical-nurse', 'health-information-specialist'],
    related_specialties: ['Clinic', 'Outpatient', 'Pediatrics', 'Medical Assistant']
  },
  {
    slug: 'tumor-registrar',
    display_name: 'Tumor Registrar',
    parent_category: 'clinical_support',
    aliases: ['certified tumor registrar', 'tumor registrar'],
    broad_title_aliases: [],
    related_professions: ['health-information-specialist'],
    related_specialties: ['Oncology', 'Administration']
  },
  {
    slug: 'health-information-specialist',
    display_name: 'Health Information Specialist',
    parent_category: 'clinical_support',
    aliases: [
      'healthcare data entry clerk', 'health information specialist',
      'health information technician', 'health information management',
      'medical records technician'
    ],
    broad_title_aliases: [],
    related_professions: ['tumor-registrar'],
    related_specialties: ['Administration', 'Clinic']
  },
  {
    slug: 'healthcare-customer-service-representative',
    display_name: 'Healthcare Customer Service Representative',
    parent_category: 'clinical_support',
    aliases: [
      'healthcare customer service representative', 'customer service representative',
      'customer service'
    ],
    broad_title_aliases: [],
    related_professions: ['health-information-specialist'],
    related_specialties: ['Administration', 'Clinic', 'Outpatient']
  }
];

/* ------------------------------------------------------------------ *
 * Credential evidence (tier 3)
 *
 * Only consulted when title AND specialty both fail. Deliberately tiny and
 * limited to unambiguous licensure/certification strings so it can never
 * invent a profession from loose prose.
 * ------------------------------------------------------------------ */

const CREDENTIAL_EVIDENCE = [
  { slug: 'registered-nurse', patterns: ['bsn, rn', 'rn license', 'registered nurse license', 'compact rn license'] },
  { slug: 'licensed-practical-nurse', patterns: ['lpn license', 'lvn license', 'practical nurse license'] },
  { slug: 'respiratory-therapist', patterns: ['rrt credential', 'registered respiratory therapist'] },
  { slug: 'surgical-technologist', patterns: ['cst certification', 'certified surgical technologist'] },
  { slug: 'radiologic-technologist', patterns: ['arrt (r)', 'arrt r', 'r.t.(r)'] },
  { slug: 'ct-technologist', patterns: ['arrt (ct)', 'arrt ct', 'r.t.(ct)'] },
  { slug: 'mri-technologist', patterns: ['arrt (mr)', 'arrt mr', 'r.t.(mr)'] },
  { slug: 'mammography-technologist', patterns: ['arrt (m)', 'arrt m'] },
  { slug: 'sonographer', patterns: ['rdms certification', 'registered diagnostic medical sonographer'] },
  { slug: 'pharmacist', patterns: ['pharmd', 'rph license'] },
  { slug: 'pharmacy-technician', patterns: ['cpht certification'] }
];

/* ------------------------------------------------------------------ *
 * Indexes (built once at module load)
 * ------------------------------------------------------------------ */

const PROFESSION_BY_SLUG = {};
for (const p of PROFESSIONS) PROFESSION_BY_SLUG[p.slug] = p;

// normalized alias -> { slug, broad }
const ALIAS_INDEX = new Map();
for (const p of PROFESSIONS) {
  const broad = new Set((p.broad_title_aliases || []).map(normalize));
  for (const alias of p.aliases) {
    const key = normalize(alias);
    if (!key) continue;
    const existing = ALIAS_INDEX.get(key);
    if (existing && existing.slug !== p.slug) {
      // An alias must never leak across professions — that would make matching
      // order-dependent. Keep the first registration and surface the clash.
      console.error(`professionTaxonomy: duplicate alias "${alias}" (${existing.slug} vs ${p.slug})`);
      continue;
    }
    ALIAS_INDEX.set(key, { slug: p.slug, broad: broad.has(key) });
  }
}

// Longest aliases first so "physical therapist assistant" beats "physical therapist".
const ALIAS_KEYS = [...ALIAS_INDEX.keys()].sort((a, b) => b.length - a.length);

/* ------------------------------------------------------------------ *
 * Primitives
 * ------------------------------------------------------------------ */

/** Lowercase, strip punctuation, collapse whitespace. */
export function normalize(value) {
  return String(value == null ? '' : value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Whole-word phrase lookup on already-normalized text. -1 when absent. */
function phraseIndex(haystack, needle) {
  if (!needle) return -1;
  return ` ${haystack} `.indexOf(` ${needle} `);
}

function collectHits(text) {
  const hits = [];
  for (const key of ALIAS_KEYS) {
    const idx = phraseIndex(text, key);
    if (idx >= 0) hits.push({ idx, len: key.length, alias: key, ...ALIAS_INDEX.get(key) });
  }
  // Earliest position wins (feed lists the primary role first), then longest.
  hits.sort((a, b) => a.idx - b.idx || b.len - a.len);
  return hits;
}

/* ------------------------------------------------------------------ *
 * Matching
 * ------------------------------------------------------------------ */

/**
 * Full explanation of how a job resolves. Never throws, never mutates.
 *
 * @returns {{
 *   profession: object|null, slug: string|null, display_name: string|null,
 *   parent_category: string|null, parent_category_name: string|null,
 *   evidence: 'title'|'specialty'|'credential'|null,
 *   conflict: boolean, deferral: boolean, multi_profession_title: boolean,
 *   title_professions: string[], specialty_professions: string[]
 * }}
 */
export function explainProfessionMatch(job) {
  const title = normalize(job?.title);
  const specialty = String(job?.specialty == null ? '' : job.specialty);

  const titleHits = title ? collectHits(title) : [];
  const titleProfessions = [...new Set(titleHits.map((h) => h.slug))];
  const topTitle = titleHits[0] || null;

  // Specialty: first comma-separated token that yields any profession wins.
  const specialtyProfessions = [];
  let topSpecialty = null;
  for (const rawToken of specialty.split(',')) {
    const token = normalize(rawToken);
    if (!token) continue;
    const hits = collectHits(token);
    if (hits.length) {
      topSpecialty = hits[0];
      for (const h of hits) if (!specialtyProfessions.includes(h.slug)) specialtyProfessions.push(h.slug);
      break;
    }
  }

  let slug = null;
  let evidence = null;
  let conflict = false;
  let deferral = false;

  if (topTitle && topSpecialty) {
    if (topTitle.slug === topSpecialty.slug) {
      slug = topTitle.slug;
      evidence = 'title+specialty';
    } else if (topTitle.broad) {
      // Broad bucket title (e.g. "Social Work") defers to the specific specialty.
      slug = topSpecialty.slug;
      evidence = 'specialty';
      deferral = true;
    } else {
      slug = topTitle.slug;
      evidence = 'title';
      conflict = true;
    }
  } else if (topTitle) {
    slug = topTitle.slug;
    evidence = 'title';
  } else if (topSpecialty) {
    slug = topSpecialty.slug;
    evidence = 'specialty';
  } else {
    const prose = normalize(`${job?.requirements || ''} ${job?.description || ''}`);
    if (prose) {
      for (const entry of CREDENTIAL_EVIDENCE) {
        if (entry.patterns.some((p) => phraseIndex(prose, normalize(p)) >= 0)) {
          slug = entry.slug;
          evidence = 'credential';
          break;
        }
      }
    }
  }

  const profession = slug ? PROFESSION_BY_SLUG[slug] : null;
  const parentKey = profession ? profession.parent_category : null;

  return {
    profession,
    slug: profession ? profession.slug : null,
    display_name: profession ? profession.display_name : null,
    parent_category: parentKey,
    parent_category_name: parentKey ? PROFESSION_CATEGORIES[parentKey] : null,
    evidence,
    conflict,
    deferral,
    multi_profession_title: titleProfessions.length > 1,
    title_professions: titleProfessions,
    specialty_professions: specialtyProfessions
  };
}

/* ------------------------------------------------------------------ *
 * Shared helpers — the public API for every future SEO feature
 * ------------------------------------------------------------------ */

/** Canonical profession object for a job, or null when unmapped. */
export function getCanonicalProfession(job) {
  return explainProfessionMatch(job).profession;
}

/** Canonical slug for a job, or null when unmapped. */
export function getProfessionSlug(job) {
  return explainProfessionMatch(job).slug;
}

/** Human-readable profession name, or null when unmapped. */
export function getProfessionDisplayName(job) {
  return explainProfessionMatch(job).display_name;
}

/** Parent category object { slug, display_name }, or null. */
export function getProfessionParent(job) {
  const parent = explainProfessionMatch(job).parent_category;
  return parent ? { slug: parent, display_name: PROFESSION_CATEGORIES[parent] } : null;
}

/** Whether the job resolves to a canonical profession. */
export function isProfessionMapped(job) {
  return explainProfessionMatch(job).slug !== null;
}

/** Related professions for a profession object or slug. */
export function getRelatedProfessions(profession) {
  const p = typeof profession === 'string' ? PROFESSION_BY_SLUG[profession] : profession;
  if (!p) return [];
  return (p.related_professions || []).map((s) => PROFESSION_BY_SLUG[s]).filter(Boolean);
}

/** Related clinical settings/modalities for a profession object or slug. */
export function getRelatedSpecialties(profession) {
  const p = typeof profession === 'string' ? PROFESSION_BY_SLUG[profession] : profession;
  return p ? (p.related_specialties || []) : [];
}

export function getProfessionBySlug(slug) {
  return PROFESSION_BY_SLUG[slug] || null;
}

export function getProfessionsByCategory(categorySlug) {
  return PROFESSIONS.filter((p) => p.parent_category === categorySlug);
}

/** All professions, for sitemaps and index pages. */
export function listProfessions() {
  return PROFESSIONS.map((p) => ({
    slug: p.slug,
    display_name: p.display_name,
    parent_category: p.parent_category,
    parent_category_name: PROFESSION_CATEGORIES[p.parent_category]
  }));
}

/**
 * Ingestion helper. Returns the job payload plus the derived canonical slug,
 * WITHOUT touching any raw source value. The canonical value is also derivable
 * at read time, so this only pre-populates the queryable field.
 */
export function withNormalizedProfession(jobData) {
  return { ...jobData, normalized_profession: getProfessionSlug(jobData) || '' };
}